import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDoctorData } from '@/hooks/useDoctorData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Building, Award, GraduationCap, Globe, Upload, Plus, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { EditableField } from '@/components/doctor/EditableField';
import { EditableTextArea } from '@/components/doctor/EditableTextArea';
import { EditableTagList } from '@/components/doctor/EditableTagList';
import ProfileShareButton from '@/components/shared/ProfileShareButton';
import { createDoctorDeepLink } from '@/utils/deepLinkUtils';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import DoctorStories from '@/components/doctor/PatientView/DoctorStories';
import DoctorActionButtons from '@/components/doctor/PatientView/DoctorActionButtons';
import DoctorBeforeAfterGallery from '@/components/doctor/PatientView/DoctorBeforeAfterGallery';
import DoctorReviewsTab from '@/components/doctor/PatientView/DoctorReviewsTab';
import DoctorAboutTab from '@/components/doctor/PatientView/DoctorAboutTab';

const DoctorProfile = () => {
  const { id } = useParams();
  const doctorId = id || "1";
  const { doctorData, updateDoctorData } = useDoctorData(doctorId);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [showImageUploadDialog, setShowImageUploadDialog] = useState(false);
  const [showClinicChangeDialog, setShowClinicChangeDialog] = useState(false);
  const [newProfileImage, setNewProfileImage] = useState("");
  const [tempProfileImage, setTempProfileImage] = useState("");
  const [reasonForClinicChange, setReasonForClinicChange] = useState("");
  const [showMobileAddStoryButton, setShowMobileAddStoryButton] = useState(window.innerWidth < 768);
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);

  const deepLink = createDoctorDeepLink(doctorId);

  React.useEffect(() => {
    const handleResize = () => {
      setShowMobileAddStoryButton(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleUpdateField = (field: string, value: string) => {
    updateDoctorData({ [field]: value });
    toast({
      title: "Profile updated",
      description: `Your ${field} has been updated successfully.`,
    });
  };

  const handleUpdateTagList = (field: string, values: string[]) => {
    updateDoctorData({ [field]: values });
    toast({
      title: "Profile updated",
      description: `Your ${field} has been updated successfully.`,
    });
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setTempProfileImage(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProfileImage = () => {
    setNewProfileImage(tempProfileImage);
    updateDoctorData({ image: tempProfileImage });
    setShowImageUploadDialog(false);
    toast({
      title: "Profile image updated",
      description: "Your profile image has been updated successfully.",
    });
  };

  const handleClinicChangeRequest = () => {
    setShowClinicChangeDialog(false);
    toast({
      title: "Clinic change requested",
      description: "Your request to change clinic has been sent for approval.",
    });
    setReasonForClinicChange("");
  };

  const calculateYearsOfExperience = () => {
    const experienceText = doctorData.experience[0] || "";
    const yearsMatch = experienceText.match(/(\d+)\+?\s*years?/i);
    return yearsMatch ? parseInt(yearsMatch[1]) : null;
  };

  const yearsOfExperience = calculateYearsOfExperience();

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="relative cursor-pointer group" onClick={() => setShowImageUploadDialog(true)}>
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden">
                <img 
                  src={newProfileImage || doctorData.image} 
                  alt={doctorData.name} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                <Upload className="text-white h-6 w-6" />
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col gap-2">
                <EditableField 
                  label="Doctor Name"
                  name="name"
                  value={doctorData.name}
                  onSave={(value) => handleUpdateField('name', value)}
                  className="text-2xl font-bold"
                />
                
                <EditableField 
                  label="Specialty"
                  name="specialty"
                  value={doctorData.specialty}
                  onSave={(value) => handleUpdateField('specialty', value)}
                  className="text-muted-foreground"
                />
              </div>
              
              <div className="flex items-center text-sm mt-2">
                <MapPin className="h-4 w-4 text-primary mr-1" />
                <span className="text-muted-foreground">{doctorData.clinicAddress}</span>
              </div>
              
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center">
                  <span className="text-yellow-500 flex">
                    {'★'.repeat(Math.floor(doctorData.rating))}
                    {'☆'.repeat(5 - Math.floor(doctorData.rating))}
                  </span>
                  <span className="ml-1 font-medium">{doctorData.rating}</span>
                </div>
                <span className="text-muted-foreground">({doctorData.reviews} reviews)</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mt-6">
            {yearsOfExperience && (
              <div className="flex flex-col items-center justify-center p-3 bg-muted/50 rounded-lg">
                <GraduationCap className="h-5 w-5 text-primary mb-1" />
                <EditableField 
                  label="Years Experience"
                  name="yearsExperience"
                  value={`${yearsOfExperience}`}
                  onSave={(value) => {
                    const newExperience = doctorData.experience.map((exp, index) => 
                      index === 0 ? `${value}+ years of clinical practice` : exp
                    );
                    updateDoctorData({ experience: newExperience });
                  }}
                  className="text-center"
                />
                <span className="text-xs text-muted-foreground">Experience</span>
              </div>
            )}
            
            <div className="flex flex-col items-center justify-center p-3 bg-muted/50 rounded-lg">
              <Award className="h-5 w-5 text-primary mb-1" />
              <span className="text-sm font-medium">{doctorData.education.length}</span>
              <span className="text-xs text-muted-foreground">Certifications</span>
            </div>
            
            <div className="flex flex-col items-center justify-center p-3 bg-muted/50 rounded-lg">
              <Globe className="h-5 w-5 text-primary mb-1" />
              <span className="text-sm font-medium">{doctorData.languages.length}</span>
              <span className="text-xs text-muted-foreground">Languages</span>
            </div>
          </div>
          
          <div className="mt-4 flex items-center">
            <Building className="h-4 w-4 text-primary mr-2" />
            <span className="text-sm">Works at </span>
            <a 
              href={`/clinic-profile/${doctorData.clinicId || 1}`}
              className="text-sm text-primary ml-1 hover:underline flex items-center"
            >
              {doctorData.clinic}
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowClinicChangeDialog(true)}
              className="ml-2 text-xs h-6 px-2 py-0"
            >
              Request Change
            </Button>
          </div>
        </div>
        
        {doctorData.stories && doctorData.stories.length > 0 && (
          <div className="px-6 py-4 border-b relative">
            <DoctorStories stories={doctorData.stories} editable={true} isDoctor={true} />
          </div>
        )}
        
        <div className="px-6 py-4 border-b">
          <DoctorActionButtons 
            doctorData={{ id: doctorId || '' }}
            isDoctor={true}
          />
        </div>
        
        {doctorData.beforeAfterGallery && doctorData.beforeAfterGallery.length > 0 && (
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold">Results Gallery</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/doctor-area/before-after')}
                className="text-xs"
              >
                View All
              </Button>
            </div>
            <DoctorBeforeAfterGallery gallery={doctorData.beforeAfterGallery} doctorId={doctorId} />
          </div>
        )}
        
        <Tabs defaultValue="about" className="p-6">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="about">About</TabsTrigger>
            <TabsTrigger value="professional">Professional</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
          </TabsList>
          
          <TabsContent value="about" className="pt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About Me</CardTitle>
              </CardHeader>
              <CardContent>
                <EditableTextArea
                  label="Bio"
                  name="bio"
                  value={doctorData.bio}
                  onSave={(value) => handleUpdateField('bio', value)}
                  rows={6}
                />
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Education</CardTitle>
                </CardHeader>
                <CardContent>
                  <EditableTagList
                    label="Education & Training"
                    items={doctorData.education}
                    onSave={(items) => handleUpdateTagList('education', items)}
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <EditableTagList
                    label="Professional Experience"
                    items={doctorData.experience}
                    onSave={(items) => handleUpdateTagList('experience', items)}
                  />
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Languages</CardTitle>
              </CardHeader>
              <CardContent>
                <EditableTagList
                  label="Languages Spoken"
                  items={doctorData.languages}
                  onSave={(items) => handleUpdateTagList('languages', items)}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="professional" className="pt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Professional Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <EditableField
                  label="License Number"
                  name="licenseNumber"
                  value={doctorData.licenseNumber || "DDS12345-NY"}
                  onSave={(value) => handleUpdateField('licenseNumber', value)}
                />
                
                <EditableTagList
                  label="Board Certifications"
                  items={doctorData.boardCertifications || ["American Board of Dental Specialties", "Board of Cosmetic Dentistry"]}
                  onSave={(items) => handleUpdateTagList('boardCertifications', items)}
                />
                
                <EditableTagList
                  label="Insurance Plans Accepted"
                  items={doctorData.insuranceAccepted || ["Aetna", "Blue Cross", "Cigna", "Delta Dental"]}
                  onSave={(items) => handleUpdateTagList('insuranceAccepted', items)}
                />
                
                <EditableTagList
                  label="Hospital Affiliations"
                  items={doctorData.hospitalAffiliations || ["New York Presbyterian", "Mount Sinai Hospital"]}
                  onSave={(items) => handleUpdateTagList('hospitalAffiliations', items)}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reviews" className="pt-6">
            <DoctorReviewsTab 
              rating={doctorData.rating}
              reviewsCount={doctorData.reviews}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      <Dialog open={showImageUploadDialog} onOpenChange={setShowImageUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative mx-auto w-40 h-40 rounded-lg overflow-hidden border">
              {tempProfileImage ? (
                <img 
                  src={tempProfileImage} 
                  alt="New profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <img 
                  src={newProfileImage || doctorData.image} 
                  alt={doctorData.name} 
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex justify-center">
              <label htmlFor="profile-image" className="cursor-pointer">
                <div className="flex items-center justify-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90">
                  <Upload className="h-4 w-4 mr-2" />
                  <span>Choose Image</span>
                </div>
                <input 
                  id="profile-image" 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleProfileImageChange}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setTempProfileImage("");
              setShowImageUploadDialog(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={saveProfileImage}
              disabled={!tempProfileImage}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showClinicChangeDialog} onOpenChange={setShowClinicChangeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Clinic Change</DialogTitle>
            <DialogDescription>
              Please provide a reason for your clinic change request. This will be reviewed by an administrator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="reason" className="block text-sm font-medium">
                Reason for Change
              </label>
              <Textarea
                id="reason"
                value={reasonForClinicChange}
                onChange={(e) => setReasonForClinicChange(e.target.value)}
                rows={4}
                placeholder="Please explain why you need to change your clinic..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClinicChangeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClinicChangeRequest}
              disabled={!reasonForClinicChange.trim()}
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Card>
        <CardHeader>
          <CardTitle>Share Your Profile</CardTitle>
          <CardDescription>
            Share your profile with patients or colleagues
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input 
                value={deepLink} 
                readOnly 
                className="bg-muted"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
            </div>
            <ProfileShareButton
              deepLink={deepLink}
              entityName={doctorData.name}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DoctorProfile;
